import { type SyntheticEvent, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { Typography } from '@gto/ui/typography'

import type { AffiliateAdminPartnerDetailSchema } from '@/api/gen'
import { useListTiers } from '@/api/gen'
import { SelectField, TextField } from '@/components/form'
import { MutationError } from '@/components/mutation-error'
import { SectionCard } from '@/components/section-card'
import { parsePercent } from '@/utils/parse-percent'
import { toRate } from '@/utils/to-rate'

import { SectionHeader } from '../section-header'
import { StackedField } from '../stacked-field'
import { usePartnerUpdate } from '../use-partner-update'
import { formatRate } from './format-rate'

type TierCommissionCardProps = {
  className?: string
  partner: Pick<
    AffiliateAdminPartnerDetailSchema,
    'tier' | 'commission_rate_override' | 'effective_commission_rate'
  >
  partnerId: string
}

export const TierCommissionCard = ({ className, partner, partnerId }: TierCommissionCardProps) => {
  const { formatMessage } = useIntl()
  const { data: tiers } = useListTiers()
  const [editing, setEditing] = useState(false)
  const [tierId, setTierId] = useState<number | ''>('')
  const [override, setOverride] = useState('')
  const [overrideInvalid, setOverrideInvalid] = useState(false)
  const { mutation, hasError } = usePartnerUpdate(partnerId, () => setEditing(false))

  const startEditing = () => {
    setTierId(partner.tier?.id ?? '')
    setOverride(
      partner.commission_rate_override == null ? '' : String(partner.commission_rate_override),
    )
    setOverrideInvalid(false)
    setEditing(true)
    mutation.reset()
  }

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault()
    // Validate the override BEFORE calling mutate: `Number('abc') → NaN` would
    // otherwise serialise to `null` and silently clear the operator's override
    // to the tier default with no error and no Sentry. Empty input → null
    // (intentional clear) stays allowed via `allowEmpty: true`.
    const parsed = parsePercent(override, { allowEmpty: true })
    if (!parsed.ok) {
      setOverrideInvalid(true)

      return
    }
    setOverrideInvalid(false)
    mutation.mutate({
      partnerId,
      data: {
        tier_id: tierId === '' ? null : tierId,
        commission_rate_override: parsed.value,
      },
    })
  }

  const tierList = tiers ?? []
  const hasOverride = partner.commission_rate_override != null
  const tierRate =
    partner.tier == null
      ? null
      : toRate(partner.tier.commission_rate, 'partner-detail tier commission_rate:')

  return (
    <SectionCard
      className={className}
      onSubmit={editing ? handleSubmit : undefined}
    >
      <SectionHeader
        editing={editing}
        saving={mutation.isPending}
        title={<FormattedMessage id="admin.partnerDetail.tierCommission" />}
        onCancel={() => setEditing(false)}
        onEdit={startEditing}
        onSave={() => undefined}
      />
      {editing ? (
        <div className="flex flex-col gap-3">
          <StackedField label={formatMessage({ id: 'admin.partnerDetail.tier' })}>
            <SelectField
              value={tierId === '' ? '' : String(tierId)}
              options={tierList.map((t) => ({
                value: String(t.id),
                label: `${t.name} (${formatRate(t.commission_rate)})`,
              }))}
              onValueChange={(v) => setTierId(v === '' ? '' : Number(v))}
            />
          </StackedField>
          <StackedField label={formatMessage({ id: 'admin.partnerDetail.customRateOverride' })}>
            <TextField
              data-tst="commission-rate-override"
              helperText={formatMessage({ id: 'admin.partnerDetail.customRateOverrideHint' })}
              max={100}
              min={0}
              step={0.01}
              type="number"
              value={override}
              error={
                overrideInvalid
                  ? formatMessage({ id: 'admin.partnerDetail.commissionRateOverride.invalid' })
                  : undefined
              }
              onChange={(e) => {
                setOverride(e.target.value)
                if (overrideInvalid) {
                  setOverrideInvalid(false)
                }
              }}
            />
          </StackedField>
          <MutationError
            messageId="admin.partnerDetail.save.error"
            show={hasError}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <StackedField label={formatMessage({ id: 'admin.partnerDetail.tier' })}>
            {partner.tier == null ? (
              <Typography
                color="text.disabled"
                variant="body2"
              >
                <FormattedMessage id="admin.partnerDetail.none" />
              </Typography>
            ) : (
              <Typography variant="body2">
                {partner.tier.name}
                {tierRate != null && <span className="text-text-secondary"> · {tierRate}%</span>}
              </Typography>
            )}
          </StackedField>
          <StackedField label={formatMessage({ id: 'admin.partnerDetail.customRateOverride' })}>
            {hasOverride ? (
              <Typography
                className="font-mono"
                variant="body2"
              >
                {toRate(
                  partner.commission_rate_override,
                  'partner-detail commission_rate_override:',
                )}
                %
              </Typography>
            ) : (
              <Typography
                color="text.disabled"
                variant="body2"
              >
                <FormattedMessage id="admin.partnerDetail.none" />
              </Typography>
            )}
          </StackedField>
          <StackedField label={formatMessage({ id: 'admin.partnerDetail.effectiveRate' })}>
            <Typography
              color="primary"
              variant="body2"
            >
              <span className="font-mono">
                {toRate(
                  partner.effective_commission_rate,
                  'partner-detail effective_commission_rate:',
                )}
                %
              </span>
              <span className="text-text-secondary">
                {' '}
                ·{' '}
                {hasOverride ? (
                  <FormattedMessage id="admin.partnerDetail.effectiveRate.override" />
                ) : (
                  <FormattedMessage id="admin.partnerDetail.effectiveRate.tierDefault" />
                )}
              </span>
            </Typography>
          </StackedField>
        </div>
      )}
    </SectionCard>
  )
}
